import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { UsernatorPromiseClient } from "../generated/user_grpc_web_pb";
import { DeleteAccountRequest, UserData } from "../generated/user_pb";
import GRPC_BACKEND_URL from "../grpcConfig";
import type { RpcError } from "grpc-web";
import { hashStringSHA256, isNetworkError } from "@dowty/core";
import type { AuthUser } from "../context/AuthContext";

// Shared by every caller of this hook (and by FriendsPage.tsx directly for addFriend/removeFriend/
// getAllUsers) rather than each constructing its own — one grpc-web client is all this needs.
export const usernatorClient = new UsernatorPromiseClient(GRPC_BACKEND_URL, null, null);

const userRequest = (user: AuthUser): UserData => {
    const request = new UserData();
    request.setUserid(user.userId);
    request.setUsername(user.username);
    request.setToken(user.token);
    return request;
};

export async function getAccountDeletionStatus(user: AuthUser): Promise<string | null> {
    const response = await usernatorClient.getDeletionStatus(userRequest(user), {});
    return response.getScheduledfor() || null;
}

export async function requestAccountDeletion(user: AuthUser, password: string): Promise<void> {
    const request = new DeleteAccountRequest();
    request.setUser(userRequest(user));
    request.setPassword(await hashStringSHA256(password));
    await usernatorClient.requestAccountDeletion(request, {});
}

export async function cancelAccountDeletion(user: AuthUser): Promise<void> {
    await usernatorClient.cancelAccountDeletion(userRequest(user), {});
}

export async function exportAccountData(user: AuthUser): Promise<{ fileName: string; json: string }> {
    const response = await usernatorClient.exportMyData(userRequest(user), {});
    return { fileName: response.getFilename(), json: response.getJson() };
}

/** Loads the current user's friend list straight from Saufdings' Usernator service via grpc-web —
 * same client-side call FriendsPage.tsx already made before switching to this hook, reused here so
 * any feature needing "which of my friends can I do X with" (e.g. DualCam album sharing) doesn't
 * need its own backend proxy for something that's already a working direct call.
 *
 * `autoLoad` (default true) loads on mount, matching FriendsPage's own always-visible friend list.
 * Pass false for a caller that only needs the list once some other UI is opened (e.g. a share-modal
 * friend picker) — otherwise every visit to a page that merely *might* show that picker pays a
 * gRPC round trip whether or not the picker is ever opened. */
export function useFriends(autoLoad = true) {
    const { user } = useAuth();
    const [friends, setFriends] = useState<UserData.AsObject[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        setError(null);
        try {
            const req = new UserData();
            req.setUserid(user.userId);
            req.setUsername(user.username);
            req.setToken(user.token);
            const res = await usernatorClient.getFriends(req, {});
            setFriends(res.getUserList().map((f: UserData) => f.toObject()));
        } catch (err) {
            if (isNetworkError(err)) setError(navigator.language.toLowerCase().startsWith("de") ? "Keine Verbindung. Bitte erneut versuchen." : "Connection failed. Please retry.");
            else setError((err as RpcError).message ?? "Failed to load friends.");
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (autoLoad) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load, autoLoad]);

    return { friends, loading, error, reload: load };
}
